<template>
  <div>
    <FormLabel v-if="label" :label="label" :labelHint="labelHint" :required="required"/>
    <OnClickOutside @trigger="() => show = false" class="relative w-full">
      <div tabindex="0"
           ref="selectedContainer"
           class="flex w-full cursor-pointer select-none justify-between gap-3 rounded-lg px-4 py-2 focus:outline-none ring-offset-2 focus:ring-2 focus:ring-bdBlue-300"
           :class="[textClass, inputClass]"
           @keydown.enter="toggleOpen"
           @click="toggleOpen">
        <span v-if="selectedOption" :class="textClass" class="truncate">{{ prefix }}{{ selectedOption }}</span>
        <span v-else class="truncate text-slate-400">{{ placeholder }}</span>
        <ChevronUpDownIcon class="-mr-2 h-6 w-6 transition-all" :class="textClass"/>
      </div>
      <TransitionScale>
        <div v-show="show && !disabled"
          class="flex w-full flex-col gap-2 rounded-lg p-2 shadow-lg z-[100] focus:outline-none"
          :class="[backgroundClass,borderClass,fixed?'fixed':'absolute top-11 right-0 left-0 mt-px']"
          :style="dropdownStyle"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabindex="-1">
          <FormInput v-model="search" @update:modelValue="debounceEmitSearch" :placeholder="searchPlaceholder"
                     :dark="dark" ref="searchHolder"/>
          <div class="overflow-y-auto max-h-[200px]" v-if="searchResults?.length">
            <div v-for="(option,index) in searchResults" :key="index"
                 class="flex w-full cursor-pointer select-none items-center gap-4 rounded-lg px-3 py-1"
                 :class="dark ? 'hover:bg-white/10' : 'hover:bg-slate-50'"
                 @click="updateModelValue(option)">
              <HoverTooltipFixed :tip="getOptionName(option)" placement="bottom" class="max-w-full">
                <span class="rounded-full h-1.5 w-1.5"
                      :class="statusIconColorClass(option)"/>
                <p class="truncate flex-1" :class="textClass">{{ getOptionName(option) }}</p>
              </HoverTooltipFixed>
            </div>
          </div>
          <div v-else class="text-center text-sm" :class="dark ? 'text-white opacity-70' : 'text-slate-400'">
            <div class="p-6">
              <slot name="empty">No results found</slot>
            </div>
          </div>
        </div>
      </TransitionScale>
    </OnClickOutside>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, onUnmounted, reactive, ref, watchEffect} from "vue";
import {debounce} from "lodash";
import {ChevronUpDownIcon} from "@heroicons/vue/24/outline";
import {OnClickOutside} from "@vueuse/components";
import TransitionScale from "./TransitionScale.vue";
import FormLabel from "./FormLabel.vue";
import FormInput from "./FormInput.vue";
import HoverTooltipFixed from "./HoverTooltipFixed.vue";

const props = defineProps({
  modelValue: [String, Number],
  label: String,
  labelHint: String,
  required: Boolean,
  options: Array,
  dark: Boolean,
  disabled: Boolean,
  showValue: Boolean,
  placeholder: {
    type: String,
    default: "Select",
  },
  searchPlaceholder: {
    type: String,
    default: "Search",
  },
  prefix: String,
  fixed: Boolean,
});

const emits = defineEmits(["update:modelValue", "search"]);
const show = ref(false);
const searchHolder = ref(null);
const selectedContainer = ref(null);

// (event) close dropdown when typing esc button.
const handleCloseOnEscape = (event) => {
  if (["Escape", "Esc"].includes(event.key)) {
    show.value = false;
  }
};

const updateModelValue = (option) => {
  const value = typeof option === "string" ? option : option.value;
  emits("update:modelValue", value);
  show.value = false;
};

const getOptionName = (option) => {
  let tip = typeof option === "string" ? option : option.name;
  return tip || "Empty";
};

const search = ref("");
const searchResults = computed(() => {
  if (search.value === "") return props.options;

  let onEmptyIndexes = [];

  const searchResultOptions = props.options.filter((option, index) => {
    if (typeof option === "string") return option.toLowerCase().includes(search.value.toLowerCase());
    if (option.onEmpty) onEmptyIndexes.push(index);
    return option.name.toLowerCase().includes(search.value.toLowerCase());
  });

  if (!searchResultOptions.length && onEmptyIndexes.length) {
    return onEmptyIndexes.map((index) => props.options[index]);
  }

  return searchResultOptions;
});

const selectedOption = computed(() => {
  if (typeof props.modelValue === "string" && !props.showValue) return props.modelValue;
  if (!props.options) return "";
  let option = props.options.find((option) => {
    return option.value === props.modelValue;
  });
  if (!option) return "";
  return option.name;
});

const inputClass = computed(() => {
  return props.dark ? "bg-[#343a40]" : "border border-slate-200 bg-slate-100";
});

const borderClass = computed(() => {
  return props.dark ? "shadow-2xl" : "border border-slate-100";
});

const textClass = computed(() => {
  return props.dark ? "text-white" : "text-slate-900";
});

const backgroundClass = computed(() => {
  return props.dark ? "bg-[#575e66]" : "bg-white";
});

const dropdownStyle = reactive({});

const setDropdownStyles = (isFixed, container) => {
  if (!isFixed || !container) return;

  const {top, left, right} = selectedContainer.value.getBoundingClientRect();

  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

  const inputHeightPx = 44;
  const dropdownDefaultMaxHeightPx = 266;
  const dropdownMenuGapPx = 6;

  if (top + inputHeightPx + dropdownDefaultMaxHeightPx + (dropdownMenuGapPx * 2) > vh) {
    Object.assign(dropdownStyle, {
      top: top - dropdownDefaultMaxHeightPx - dropdownMenuGapPx + "px",
      height: dropdownDefaultMaxHeightPx + "px",
      left: left + "px",
      width: right - left + "px",
    });
    return;
  }

  Object.assign(dropdownStyle, {
    top: top + inputHeightPx + "px",
    left: left + "px",
    width: right - left + "px",
  });
};

watchEffect(() => setDropdownStyles(props.fixed, selectedContainer.value));

const debounceEmitSearch = debounce((value) => emits("search", value), 300);

const statusIconColorClass = (option) => {
  const isSelected = typeof option === "string" ? option === props.modelValue : option.value === props.modelValue;
  if (isSelected) return "bg-green-500";
  if (props.dark) return "bg-[#343a40]";
  return "bg-slate-200";
};

const toggleOpen = async () => {
  if (props.disabled) return;
  show.value = !show.value;

  if (show.value) {
    await nextTick();
    if (!searchHolder.value) return;
    searchHolder.value.inputEl.focus();
  }
};

const reTriggerDropdownStyles = debounce(() => setDropdownStyles(props.fixed, selectedContainer.value), 100);

onMounted(() => {
  document.addEventListener("keydown", handleCloseOnEscape);
  if (props.fixed) {
    document.addEventListener("scroll", reTriggerDropdownStyles, {passive: true});
    window.addEventListener("resize", reTriggerDropdownStyles);
  }
});
onUnmounted(() => {
  document.removeEventListener("keydown", handleCloseOnEscape);
  if (props.fixed) {
    document.removeEventListener("scroll", reTriggerDropdownStyles);
    window.removeEventListener("resize", reTriggerDropdownStyles);
  }
});
</script>
